import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

const emptyForm = {
  tipoPersianaid: '', marca: '', modelo: '', material: '', cor: '',
  larguraMinCm: '', larguraMaxCm: '', alturaMinCm: '', alturaMaxCm: '',
  precoM2: '', precoInstalacao: '', garantiaMeses: '12', prazoEntregaDias: '15',
  acionamento: ['MANUAL_CORDA'], observacoes: '', ativo: true,
};

const ACIONAMENTOS = [
  { value: 'MANUAL_CORDA', label: 'Manual (Corda)' },
  { value: 'MANUAL_CORRENTE', label: 'Manual (Corrente)' },
  { value: 'MOTORIZADO', label: 'Motorizado' },
  { value: 'AUTOMATICO', label: 'Automático' },
];

export default function ProdutosPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [tipoFilter, setTipoFilter] = useState('');

  const { data: tipos = [] } = useQuery({
    queryKey: ['tipos-persiana'],
    queryFn: () => api.get('/tipos-persiana').then(r => r.data),
  });

  const { data: produtos = [], isLoading } = useQuery({
    queryKey: ['produtos', tipoFilter],
    queryFn: () => api.get('/produtos', { params: tipoFilter ? { tipoPersianaid: tipoFilter } : undefined }).then(r => r.data),
  });

  const saveMutation = useMutation({
    mutationFn: (data: any) => editId ? api.put(`/produtos/${editId}`, data) : api.post('/produtos', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['produtos'] }); setShowForm(false); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/produtos/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['produtos'] }),
  });

  function openNew() { setForm(emptyForm); setEditId(null); setShowForm(true); }
  function openEdit(p: any) {
    setForm({
      ...p,
      tipoPersianaid: p.tipoPersianaid,
      larguraMinCm: String(p.larguraMinCm), larguraMaxCm: String(p.larguraMaxCm),
      alturaMinCm: String(p.alturaMinCm), alturaMaxCm: String(p.alturaMaxCm),
      precoM2: String(p.precoM2), precoInstalacao: String(p.precoInstalacao),
      garantiaMeses: String(p.garantiaMeses), prazoEntregaDias: String(p.prazoEntregaDias),
    });
    setEditId(p.id);
    setShowForm(true);
  }

  function submitForm() {
    const data = {
      ...form,
      larguraMinCm: parseFloat(form.larguraMinCm), larguraMaxCm: parseFloat(form.larguraMaxCm),
      alturaMinCm: parseFloat(form.alturaMinCm), alturaMaxCm: parseFloat(form.alturaMaxCm),
      precoM2: parseFloat(form.precoM2), precoInstalacao: parseFloat(form.precoInstalacao),
      garantiaMeses: parseInt(form.garantiaMeses), prazoEntregaDias: parseInt(form.prazoEntregaDias),
    };
    saveMutation.mutate(data);
  }

  const field = (label: string, key: keyof typeof form, type = 'text', placeholder = '') => (
    <div key={key}>
      <label className="label">{label}</label>
      <input
        type={type}
        className="input"
        placeholder={placeholder}
        value={String(form[key])}
        onChange={e => setForm({ ...form, [key]: e.target.value })}
      />
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Produtos / Persianas</h1>
        <button onClick={openNew} className="btn-primary">+ Novo</button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        <button onClick={() => setTipoFilter('')} className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border ${!tipoFilter ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300'}`}>
          Todos
        </button>
        {tipos.map((t: any) => (
          <button key={t.id} onClick={() => setTipoFilter(t.id)} className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border ${tipoFilter === t.id ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300'}`}>
            {t.nome}
          </button>
        ))}
      </div>

      {isLoading ? <div className="text-center py-8 text-gray-400">Carregando...</div> : (
        <div className="space-y-2">
          {produtos.map((p: any) => (
            <div key={p.id} className={`card ${!p.ativo ? 'opacity-60' : ''}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium">{p.marca} — {p.modelo}</div>
                  <div className="text-sm text-gray-500">{p.tipoPersiana?.nome} · {p.material} · {p.cor}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    {p.larguraMinCm}–{p.larguraMaxCm}cm larg · {p.alturaMinCm}–{p.alturaMaxCm}cm alt
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-primary-700">R$ {p.precoM2}/m²</div>
                  <div className="text-xs text-gray-400">Inst. R$ {p.precoInstalacao}</div>
                </div>
              </div>
              <div className="flex gap-2 mt-2">
                <button onClick={() => openEdit(p)} className="text-sm text-blue-500">Editar</button>
                <button onClick={() => deleteMutation.mutate(p.id)} className="text-sm text-red-500">
                  {p.ativo ? 'Desativar' : 'Ativar'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-end md:items-center justify-center">
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full md:max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between">
              <h3 className="font-semibold">{editId ? 'Editar Produto' : 'Novo Produto'}</h3>
              <button onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="label">Tipo de Persiana *</label>
                <select className="input" value={form.tipoPersianaid} onChange={e => setForm({...form, tipoPersianaid: e.target.value})}>
                  <option value="">Selecione...</option>
                  {tipos.map((t: any) => <option key={t.id} value={t.id}>{t.nome}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {field('Marca *', 'marca')}
                {field('Modelo *', 'modelo')}
                {field('Material', 'material', 'text', 'Tecido, Alumínio, PVC...')}
                {field('Cor', 'cor', 'text', 'Branco, Cinza, Bege...')}
                {field('Largura mín (cm)', 'larguraMinCm', 'number')}
                {field('Largura máx (cm)', 'larguraMaxCm', 'number')}
                {field('Altura mín (cm)', 'alturaMinCm', 'number')}
                {field('Altura máx (cm)', 'alturaMaxCm', 'number')}
                {field('Preço por m² (R$) *', 'precoM2', 'number')}
                {field('Preço instalação (R$)', 'precoInstalacao', 'number')}
                {field('Garantia (meses)', 'garantiaMeses', 'number')}
                {field('Prazo entrega (dias)', 'prazoEntregaDias', 'number')}
              </div>
              <div>
                <label className="label">Acionamentos disponíveis</label>
                <div className="grid grid-cols-2 gap-2">
                  {ACIONAMENTOS.map(a => (
                    <label key={a.value} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.acionamento.includes(a.value)}
                        onChange={e => setForm({
                          ...form,
                          acionamento: e.target.checked
                            ? [...form.acionamento, a.value]
                            : form.acionamento.filter(x => x !== a.value),
                        })}
                      />
                      {a.label}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="label">Observações</label>
                <textarea className="input resize-none" rows={2} value={form.observacoes} onChange={e => setForm({...form, observacoes: e.target.value})} />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowForm(false)} className="btn-secondary flex-1">Cancelar</button>
                <button onClick={submitForm} disabled={!form.tipoPersianaid || !form.marca || !form.modelo || saveMutation.isPending} className="btn-primary flex-1">
                  {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
